import { useState } from "react";
import { XMarkIcon, ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";
import { theme } from "@/lib/theme";
import type { Podcast } from "@/hooks/usePodcasts";

interface NewSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (podcastSlug: string) => void;
  podcasts: Podcast[];
  currentPodcast: Podcast | null;
  isCreating: boolean;
  processingPodcastIds?: Set<string>;
}

export default function NewSessionModal({
  isOpen,
  onClose,
  onConfirm,
  podcasts,
  currentPodcast,
  isCreating,
  processingPodcastIds,
}: NewSessionModalProps) {
  const [selectedPodcast, setSelectedPodcast] = useState<Podcast | null>(
    currentPodcast,
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selectedPodcast) {
      onConfirm(selectedPodcast.id);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={cn(
            "w-full max-w-md",
            theme.cards.base,
            "p-6",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              New Chat Session
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Podcast selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select podcast
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={cn(
                  "w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg",
                  "bg-white dark:bg-gray-800",
                  "border-2 border-gray-300 dark:border-gray-600",
                  "text-left text-gray-900 dark:text-white",
                  "hover:border-gray-400 dark:hover:border-gray-500",
                  "focus:outline-none focus:ring-2 focus:ring-primary-orange dark:focus:ring-primary-orange-dark",
                )}
              >
                {selectedPodcast ? (
                  <span className="flex items-center gap-2 truncate">
                    <span className="text-lg">{selectedPodcast.emoji}</span>
                    <span>{selectedPodcast.name}</span>
                  </span>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400">
                    Choose a podcast...
                  </span>
                )}
                <ChevronDownIcon
                  className={cn(
                    "w-5 h-5 text-gray-400 transition-transform",
                    isDropdownOpen && "rotate-180",
                  )}
                />
              </button>

              {/* Dropdown */}
              {isDropdownOpen && (
                <div
                  className={cn(
                    "absolute top-full left-0 right-0 mt-1 z-10",
                    "bg-white dark:bg-gray-800 rounded-lg",
                    "border-2 border-gray-300 dark:border-gray-600",
                    "shadow-lg max-h-60 overflow-y-auto",
                  )}
                >
                  {podcasts.map((podcast, index) => {
                    const isProcessing = processingPodcastIds?.has(podcast.id);
                    return (
                      <button
                        key={podcast.id ?? `podcast-${index}`}
                        onClick={() => {
                          if (!isProcessing) {
                            setSelectedPodcast(podcast);
                            setIsDropdownOpen(false);
                          }
                        }}
                        disabled={isProcessing}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2.5 text-left",
                          isProcessing
                            ? "opacity-60 cursor-not-allowed bg-gray-50 dark:bg-gray-800"
                            : "hover:bg-gray-100 dark:hover:bg-gray-700",
                          podcast.id === selectedPodcast?.id &&
                            !isProcessing &&
                            "bg-primary-orange/10 dark:bg-primary-orange-dark/10",
                        )}
                      >
                        <span className="text-lg">{podcast.emoji}</span>
                        <span
                          className={cn(
                            "flex-1 truncate",
                            isProcessing
                              ? "text-gray-500 dark:text-gray-400"
                              : "text-gray-900 dark:text-white",
                          )}
                        >
                          {podcast.name}
                        </span>
                        {isProcessing ? (
                          <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 font-medium">
                            <span className="w-3 h-3 border-2 border-amber-600 dark:border-amber-400 border-t-transparent rounded-full animate-spin" />
                            Processing
                          </span>
                        ) : podcast.id === selectedPodcast?.id ? (
                          <CheckIcon className="w-5 h-5 text-primary-orange dark:text-primary-orange-dark" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {currentPodcast && selectedPodcast?.id !== currentPodcast.id && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                This will switch your current podcast context.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isCreating}
              className={cn(
                "flex-1 px-4 py-2.5 rounded-lg font-medium",
                theme.buttons.secondary,
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedPodcast || isCreating}
              className={cn(
                "flex-1 px-4 py-2.5 rounded-lg font-medium",
                theme.buttons.primary,
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              {isCreating ? "Creating..." : "Create Session"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
